import { create, el, inject, awaitRedraw } from "@dhx/ts-common/dom";
import { Layout } from "@dhx/ts-layout";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { Checkbox } from "./checkbox";
import { Label } from "./helper/label";
import {
	ItemEvent,
	ICheckboxGroupEventHandlersMap,
	ICheckboxGroup,
	ICheckboxGroupConfig,
	ValidationStatus,
	ICheckboxGroupValue,
	ICheckboxGroupProps,
	ICheckboxGroupItemProps,
} from "../types";
import { isEmptyObj, uid } from "@dhx/ts-common/core";
import { baseProps, getFormItemCss, getValidationMessage, isVerify } from "../helper";
import { Popup } from "@dhx/ts-popup";

export class CheckboxGroup extends Label implements ICheckboxGroup {
	public config: ICheckboxGroupConfig;
	public layout: Layout;
	public events: IEventSystem<ItemEvent, ICheckboxGroupEventHandlersMap> = new EventSystem();

	private _buttons: Checkbox[] = [];
	private _isValid = true;
	private _propsItem = [
		"required",
		"label",
		"labelWidth",
		"labelPosition",
		"hiddenLabel",
		"helpMessage",
		"preMessage",
		"successMessage",
		"errorMessage",
		"options",
	];
	private _props = [...baseProps, ...this._propsItem];

	constructor(container, config: ICheckboxGroupConfig) {
		super(null, config);

		this._initView(config);
		this._initHandlers();

		const render = () => this._draw();
		this.mount(container, create({ render }));
	}
	destructor(): void {
		this.events && this.events.clear();
		this.layout && this.layout.destructor();
		this.events = this._uid = this._propsItem = this._props = this._isValid = this._buttons = null;
		super._destructor();
		this.unmount();
	}
	setProperties(arg?: string | ICheckboxGroupProps, props?: ICheckboxGroupItemProps): void {
		if (
			typeof arg === "undefined" ||
			!this.events.fire(ItemEvent.beforeChangeProperties, [this.getProperties()])
		) {
			return;
		}

		if (typeof arg === "object" && !isEmptyObj(arg)) {
			for (const key in arg) {
				if (this._props.includes(key)) {
					this.config[key] = arg[key];
				}
			}
			if (arg.hasOwnProperty("options")) {
				this._initView(this.config);
				this._initHandlers();
			}
		}
		if (typeof arg === "string" && props && !isEmptyObj(props)) {
			const checkbox = this._buttons.find(item => item.config.id === arg);
			checkbox && checkbox.setProperties(props, true);
		}

		this.events.fire(ItemEvent.afterChangeProperties, [this.getProperties()]);
		this.paint();
	}
	getProperties(id?: string): ICheckboxGroupProps | ICheckboxGroupItemProps {
		if (typeof id === "undefined") {
			const obj: ICheckboxGroupItemProps = {};
			for (const key in this.config) {
				if (this._props.includes(key)) {
					obj[key] = this.config[key];
				}
			}
			return obj;
		}
		const checkbox = this._buttons.find(item => item.config.id === id);
		return checkbox.getProperties();
	}
	getValue(): ICheckboxGroupValue;
	getValue(id: string): string | boolean;
	getValue(id?: string): ICheckboxGroupValue | string | boolean {
		if (typeof id === "undefined") {
			const valueList = {};
			this._buttons.forEach(item => {
				valueList[item.config.id] = item.getValue();
			});
			return valueList;
		}
		const checkbox = this._buttons.find(item => item.config.id === id);
		if (id && checkbox) {
			return checkbox.getValue();
		}
	}
	setValue(newValue: ICheckboxGroupValue): void {
		if (typeof newValue === "undefined" || isEmptyObj(newValue)) return;

		let isSet = false;
		for (const [id, value] of Object.entries(newValue)) {
			const checkbox = this._buttons.find(item => item.config.id === id);
			if (checkbox) {
				checkbox.setValue(!!value, true);
				isSet = true;
			}
		}
		if (isSet) {
			this.events.fire(ItemEvent.change, [this.getValue()]);
			isVerify(this.config) && this.validate();
		}
	}
	isChecked(id?: string): boolean | { [key: string]: boolean } {
		if (typeof id === "undefined") {
			const checkedList = {};
			this._buttons.forEach(item => {
				checkedList[item.config.id] = item.isChecked();
			});
			return checkedList;
		}
		const checkbox = this._buttons.find(item => item.config.id === id);
		if (id && checkbox) {
			return checkbox.isChecked();
		}
	}
	focus(id?: string) {
		awaitRedraw().then(() => {
			if (!this._buttons.length) {
				return;
			}
			if (!id) {
				return this._buttons[0].focus();
			}
			const checkbox = this._buttons.find(item => item.config.id === id);
			checkbox && checkbox.focus();
		});
	}
	blur() {
		awaitRedraw().then(() => {
			if (!this._buttons.length) {
				return;
			}
			this._buttons.forEach(checkbox => {
				checkbox.blur();
			});
		});
	}
	show(): void {
		const { hidden } = this.config;
		if (!hidden || !this.events.fire(ItemEvent.beforeShow, [this.getValue()])) {
			return;
		}
		this.config.hidden = false;
		this.events.fire(ItemEvent.afterShow, [this.getValue()]);
	}
	hide(init?: boolean): void {
		const { hidden } = this.config;
		if ((hidden && !init) || !this.events.fire(ItemEvent.beforeHide, [this.getValue(), init])) {
			return;
		}
		this.config.hidden = true;
		this.events.fire(ItemEvent.afterHide, [this.getValue(), init]);
	}
	isVisible(): boolean {
		return !this.config.hidden;
	}
	disable(): void {
		this.config.disabled = true;
		this.paint();
	}
	enable(): void {
		this.config.disabled = false;
		if (this._buttons.length) {
			this._buttons.forEach(button => {
				button.enable();
			});
		}
		this.paint();
	}
	isDisabled(): boolean {
		return this.config.disabled;
	}
	clear(): void {
		if (this._buttons.some(item => item.isChecked())) {
			this._buttons.forEach(element => {
				element.clear(true);
			});
			this.events.fire(ItemEvent.change, [this.getValue()]);
		}
	}
	validate(silent = false): boolean {
		if (!silent && !this.events.fire(ItemEvent.beforeValidate, [this.getValue()])) return;

		if (this.config.required) {
			this._isValid = this._buttons.some(
				element => element.config.$required && !!element.config.checked
			);
		}
		if (!silent) {
			this._buttons.forEach(element => {
				element.config.$validationStatus = this._isValid
					? ValidationStatus.success
					: ValidationStatus.error;
			});
			this.config.$validationStatus = this._isValid ? ValidationStatus.success : ValidationStatus.error;
			this.events.fire(ItemEvent.afterValidate, [this.getValue(), this._isValid]);
		}

		return this._isValid;
	}
	clearValidate(): void {
		this.config.$validationStatus = ValidationStatus.pre;
		this._buttons.forEach(element => {
			element.clearValidate();
		});
		this.paint();
	}
	protected _initView(config: ICheckboxGroupConfig): void {
		if (isEmptyObj(config) || !config.options || isEmptyObj(config.options)) {
			throw new Error("Check the configuration is correct");
		}
		if (this.layout) {
			this.layout.destructor();
		}
		if (this._buttons.length) {
			this._buttons.forEach(button => {
				button.destructor();
			});
			this._buttons = [];
		}
		this.config = {
			type: config.type,
			id: config.id,
			name: config.name,
			disabled: false,
			hidden: false,
			options: {},
			required: false,
			label: "",
			labelWidth: "",
			labelPosition: "top",
			hiddenLabel: false,
			helpMessage: "",
			preMessage: "",
			successMessage: "",
			errorMessage: "",
			width: "content",
			height: "content",
		};
		for (const key in config) {
			if (key !== "id" && key !== "type" && key !== "name") {
				this.config[key] = config[key];
			}
		}
		if (this.config.helpMessage) {
			this._helper = new Popup({
				css: "dhx_tooltip dhx_tooltip--forced dhx_tooltip--light",
			});
			this._helper.attachHTML(this.config.helpMessage);
		}

		const checkboxesConfig = this.config.options.rows || this.config.options.cols;

		checkboxesConfig.forEach(option => {
			option.id = option.id || uid();
			option.$group = true;
		});

		this.layout = new Layout(null, this.config.options);

		checkboxesConfig.forEach(option => {
			const checkbox = new Checkbox(null, {
				...option,
				disabled: config.disabled,
				name: config.name,
				$required: config.required,
			});

			this._buttons.push(checkbox);
			this.layout.getCell(option.id).attach(checkbox);

			checkbox.events.on(ItemEvent.change, () => {
				this.events.fire(ItemEvent.change, [this.getValue()]);
				isVerify(this.config) && this.validate();
			});
			checkbox.events.on(ItemEvent.focus, (_value, id) => {
				this.events.fire(ItemEvent.focus, [this.getValue(), id]);
			});
			checkbox.events.on(ItemEvent.blur, (_value, id) => {
				this.events.fire(ItemEvent.blur, [this.getValue(), id]);
			});
			checkbox.events.on(ItemEvent.keydown, (event, id) => {
				this.events.fire(ItemEvent.keydown, [event, id]);
			});
		});

		this.config.value && this.setValue(this.config.value);
		this.clearValidate();

		if (this.config.hidden) {
			awaitRedraw().then(() => {
				this.hide(true);
			});
		}
	}
	protected _initHandlers() {
		this.events.on(ItemEvent.change, () => {
			this.config.value = this.getValue();
			this.paint();
		});
		this.events.on(ItemEvent.afterValidate, () => {
			this.config.$validationStatus = this._isValid ? ValidationStatus.success : ValidationStatus.error;
			this.paint();
		});
	}
	protected _draw() {
		const visibility = this.config.hidden ? " dhx_form-group--hidden" : "";
		const { label, labelWidth, helpMessage, required, name, id } = this.config;
		return el("fieldset.dhx_form-group-fieldset", {}, [
			el(
				"div.dhx_form-group.dhx_form-group--checkbox-group",
				{
					class: getFormItemCss(this.config, isVerify(this.config)) + visibility,
					"data-dhx-id": name || id,
					role: "radiogroup",
				},
				[
					label || labelWidth || helpMessage || required ? this._drawLabel() : null,
					el("div.dhx_checkbox-group--container", {}, [
						this.layout && inject(this.layout.getRootView()),
						required &&
							getValidationMessage(this.config) &&
							el("span.dhx_input__caption", getValidationMessage(this.config)),
					]),
				]
			),
		]);
	}
}
